/**
 * 表格拖拽绘制：圈选区域 → 工具栏确认行列 → 插入表格（页内 / 工作区）
 */
import { useState, useRef, useCallback, useEffect, type RefObject } from 'react';
import { screenToWorld } from '@/components/canvas/document/coordinates';
import type { TextInsertVia } from '@/components/canvas/workspace/model/elementInsertTarget';
import {
  DEFAULT_TABLE_DRAW_COLS,
  DEFAULT_TABLE_DRAW_ROWS,
  MIN_TABLE_DRAW_SIZE,
} from '@/components/canvas/elements/table/utils/tableDrawDefaults';

export interface TableDrawRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TableDrawPending {
  rect: TableDrawRect;
  via: TextInsertVia;
  pageId?: string;
  rows: number;
  cols: number;
}

interface UseTableDrawOptions {
  active: boolean;
  pageRef: RefObject<HTMLDivElement | null>;
  stageHostRef?: RefObject<HTMLDivElement | null>;
  panX?: number;
  panY?: number;
  zoom: number;
  isSpacePressed: boolean;
  addTableElementAt: (payload: {
    x: number;
    y: number;
    width: number;
    height: number;
    rows: number;
    cols: number;
    via?: TextInsertVia;
    pageId?: string;
  }) => string | null;
  onDrawComplete?: () => void;
}

export function useTableDraw({
  active,
  pageRef,
  stageHostRef,
  panX = 0,
  panY = 0,
  zoom,
  isSpacePressed,
  addTableElementAt,
  onDrawComplete,
}: UseTableDrawOptions) {
  const [drawRect, setDrawRect] = useState<TableDrawRect | null>(null);
  const [pending, setPending] = useState<TableDrawPending | null>(null);

  const startRef = useRef<{ x: number; y: number } | null>(null);
  const insertTargetRef = useRef<{ via: TextInsertVia; pageId?: string }>({
    via: 'workspace',
  });
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const panXRef = useRef(panX);
  panXRef.current = panX;
  const panYRef = useRef(panY);
  panYRef.current = panY;
  const addTableRef = useRef(addTableElementAt);
  addTableRef.current = addTableElementAt;
  const onCompleteRef = useRef(onDrawComplete);
  onCompleteRef.current = onDrawComplete;

  const toDrawCoords = useCallback((clientX: number, clientY: number) => {
    const pageEl = pageRef.current;
    if (pageEl) {
      const rect = pageEl.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / zoomRef.current,
        y: (clientY - rect.top) / zoomRef.current,
      };
    }
    const host = stageHostRef?.current;
    if (host) {
      const rect = host.getBoundingClientRect();
      return screenToWorld(
        clientX,
        clientY,
        rect,
        panXRef.current,
        panYRef.current,
        zoomRef.current,
      );
    }
    return { x: 0, y: 0 };
  }, [pageRef, stageHostRef]);

  const finishSession = useCallback(() => {
    setDrawRect(null);
    setPending(null);
    startRef.current = null;
    onCompleteRef.current?.();
  }, []);

  useEffect(() => {
    if (!active) {
      startRef.current = null;
      setDrawRect(null);
      setPending(null);
      return;
    }

    const onMouseMove = (e: MouseEvent) => {
      if (!startRef.current || pending) return;
      const pos = toDrawCoords(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      setDrawRect({
        x: Math.min(sx, pos.x),
        y: Math.min(sy, pos.y),
        width: Math.abs(pos.x - sx),
        height: Math.abs(pos.y - sy),
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!startRef.current || pending) return;
      const pos = toDrawCoords(e.clientX, e.clientY);
      const { x: sx, y: sy } = startRef.current;
      startRef.current = null;
      setDrawRect(null);

      const x = Math.min(sx, pos.x);
      const y = Math.min(sy, pos.y);
      const width = Math.abs(pos.x - sx);
      const height = Math.abs(pos.y - sy);

      if (width < MIN_TABLE_DRAW_SIZE || height < MIN_TABLE_DRAW_SIZE) {
        finishSession();
        return;
      }

      const insertOpts = insertTargetRef.current;
      setPending({
        rect: { x, y, width, height },
        via: insertOpts.via,
        pageId: insertOpts.pageId,
        rows: DEFAULT_TABLE_DRAW_ROWS,
        cols: DEFAULT_TABLE_DRAW_COLS,
      });
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [active, pending, toDrawCoords, finishSession]);

  const handleCanvasMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!active || pending) return false;
      if (e.button !== 0) return false;
      if (isSpacePressed) return false;

      const target = e.target as HTMLElement;
      if (target.closest('[data-element-hit]') || target.closest('[data-handle]')) {
        return false;
      }
      if (target.closest('[data-table-draw-toolbar]')) return false;

      e.preventDefault();
      e.stopPropagation();
      if (pageRef.current) {
        insertTargetRef.current = {
          via: 'page',
          pageId: pageRef.current.getAttribute('data-page') ?? undefined,
        };
      } else {
        insertTargetRef.current = { via: 'workspace' };
      }
      startRef.current = toDrawCoords(e.clientX, e.clientY);
      setDrawRect({
        x: startRef.current.x,
        y: startRef.current.y,
        width: 0,
        height: 0,
      });
      return true;
    },
    [active, pending, isSpacePressed, toDrawCoords, pageRef],
  );

  const setPendingRows = useCallback((rows: number) => {
    setPending((p) => (p ? { ...p, rows: Math.max(1, Math.min(50, rows)) } : p));
  }, []);

  const setPendingCols = useCallback((cols: number) => {
    setPending((p) => (p ? { ...p, cols: Math.max(1, Math.min(50, cols)) } : p));
  }, []);

  const confirmPending = useCallback(() => {
    if (!pending) return;
    const { rect, rows, cols, via, pageId } = pending;
    addTableRef.current({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      rows,
      cols,
      via,
      pageId,
    });
    finishSession();
  }, [pending, finishSession]);

  const cancelPending = useCallback(() => {
    finishSession();
  }, [finishSession]);

  return {
    drawRect,
    pending,
    setPendingRows,
    setPendingCols,
    handleCanvasMouseDown,
    confirmPending,
    cancelPending,
    isDrawing: drawRect !== null,
    isPending: pending !== null,
  };
}
